import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { GALLERY_JOBS } from "@/lib/galleryJobs";

// Highlight one before/after pair per job on the homepage.
const HIGHLIGHTS = GALLERY_JOBS.map((job) => ({
  slug: job.slug,
  title: job.title,
  location: job.location,
  before: job.before[0],
  after: job.after[0],
}));

export default function HomeGalleryStrip() {
  return (
    <section
      id="see-our-work"
      className="py-16 sm:py-24 bg-[#FAF8F5]"
      data-testid="home-gallery-strip"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">
              See our work
            </p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Before &amp; after.
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              A quick look at two of our most recent clearances in and around Peterborough.
            </p>
          </div>
          <Link
            to="/gallery"
            data-testid="home-gallery-view-full"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#FF7F00] hover:bg-[#E67000] text-white font-bold shadow-md shadow-orange-200 transition-colors self-start"
          >
            View full gallery <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-10 grid lg:grid-cols-2 gap-6">
          {HIGHLIGHTS.map((h, i) => (
            <Link
              to="/gallery"
              key={h.slug}
              data-testid={`home-gallery-highlight-${i + 1}`}
              className="group rounded-3xl border border-slate-200 bg-white p-5 sm:p-6 hover:border-[#FFB870] hover:shadow-lg transition-all"
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="relative overflow-hidden rounded-2xl aspect-[4/3] bg-slate-100">
                  <img
                    src={h.before}
                    alt={`${h.title} — before`}
                    loading="lazy"
                    className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                  <span className="absolute top-2 left-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-900/85 text-white">
                    Before
                  </span>
                </div>
                <div className="relative overflow-hidden rounded-2xl aspect-[4/3] bg-slate-100">
                  <img
                    src={h.after}
                    alt={`${h.title} — after`}
                    loading="lazy"
                    className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                  <span className="absolute top-2 left-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#FF7F00] text-white">
                    After
                  </span>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between gap-3">
                <div>
                  <p className="font-display text-lg font-extrabold text-slate-900 tracking-tight">
                    {h.title}
                  </p>
                  <p className="text-sm text-slate-500">{h.location}</p>
                </div>
                <span className="inline-flex items-center gap-1 text-sm font-bold text-[#FF7F00] shrink-0">
                  See job
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
